import { z } from "zod";

export const tenantConfigurationUpdateSchema = z.object({
  tenantPlaylistCreativesPerSlot: z
    .number()
    .int()
    .min(0, "Must be a positive number")
    .optional(),
  clientPlaylistCreativeLimit: z
    .number()
    .int()
    .min(0, "Must be a positive number")
    .optional(),
  clientPlaylistCreativesPerSlot: z
    .number()
    .int()
    .min(0, "Must be a positive number")
    .optional(),
  clientCreativePlayDuration: z
    .number()
    .int()
    .min(0, "Must be a positive number")
    .optional(),
  tenantCreativePlayDuration: z
    .number()
    .int()
    .min(0, "Must be a positive number")
    .optional(),
});

export type TenantConfigurationUpdateFormData = z.infer<
  typeof tenantConfigurationUpdateSchema
>;

// ─── Tenant-level live band ──────────────────────────────────────────
// ONE always-on band per tenant, applied to all the tenant's screens. It is a
// layout, not an overlay: it reserves heightPct% of the screen at top/bottom
// and the creative is shrunk into the remaining area. The band is full width
// and shows one widget at a time (weather, live score, market, news), each for
// its own duration. Mirrors the backend contract.
export type WidgetPlacement = "top" | "bottom";
export type LiveScoreSport = "cricket" | "football";
export type BandWidgetKind = "weather" | "livescore" | "market" | "news";

/** Rotation order. Fixed: tenants only switch widgets on and off. */
export const BAND_WIDGET_ORDER: readonly BandWidgetKind[] = [
  "weather",
  "livescore",
  "market",
  "news",
];
/** Seconds a widget stays on screen, per widget. */
export const ALLOWED_WIDGET_SECONDS = [10, 15, 20, 30, 45, 60, 90, 120] as const;
export type BandWidgetDurations = Record<BandWidgetKind, number>;
export const DEFAULT_WIDGET_SECONDS: Readonly<BandWidgetDurations> = {
  weather: 15,
  livescore: 20,
  market: 20,
  news: 60,
};
export const ALLOWED_BAND_HEIGHT_PCTS = [5, 7.5, 10, 12.5] as const;
export const DEFAULT_BAND_HEIGHT_PCT = 12.5;
// Market widget: one column per symbol. Mirrors the backend defaults and cap.
export const DEFAULT_MARKET_SYMBOLS = ["^NSEI", "^BSESN", "^IXIC"];
export const MAX_MARKET_SYMBOLS = 6;
// NewsData.io headline categories. Mirrors the backend NEWS_CATEGORIES.
export const NEWS_CATEGORIES = [
  "top",
  "business",
  "sports",
  "technology",
  "entertainment",
  "health",
  "science",
  "politics",
  "world",
] as const;
export type NewsCategory = (typeof NEWS_CATEGORIES)[number];
export const DEFAULT_NEWS_CATEGORY: NewsCategory = "top";

export interface TenantLiveBand {
  enabled: boolean;
  placement?: WidgetPlacement;
  heightPct?: number;
  /** Enabled widgets; absent = all four */
  widgets?: BandWidgetKind[] | null;
  /** Seconds on screen per widget; missing = default */
  durations?: Partial<BandWidgetDurations> | null;
  /** null matchId = first live match */
  livescore?: { sport: LiveScoreSport; matchId?: string | null } | null;
  /** null feedUrl = NewsData by category (or the server's default RSS feed) */
  news?: { feedUrl?: string | null; category?: NewsCategory | null } | null;
  /** null symbols = server default (NIFTY 50, SENSEX, NASDAQ) */
  market?: { symbols?: string[] | null } | null;
}

export interface TenantLiveWidgets {
  band?: TenantLiveBand | null;
}

/** Fully-populated band, as edited by the portal form and sent to the API. */
export interface NormalizedLiveBand {
  enabled: boolean;
  placement: WidgetPlacement;
  heightPct: number;
  widgets: BandWidgetKind[];
  durations: BandWidgetDurations;
  livescore: { sport: LiveScoreSport; matchId: string | null };
  news: { feedUrl: string | null; category: NewsCategory };
  market: { symbols: string[] | null };
}

export const DEFAULT_LIVE_BAND: NormalizedLiveBand = {
  enabled: false,
  placement: "bottom",
  heightPct: DEFAULT_BAND_HEIGHT_PCT,
  widgets: [...BAND_WIDGET_ORDER],
  durations: { ...DEFAULT_WIDGET_SECONDS },
  livescore: { sport: "cricket", matchId: null },
  news: { feedUrl: null, category: DEFAULT_NEWS_CATEGORY },
  market: { symbols: null },
};

const isObject = (v: unknown): v is Record<string, unknown> =>
  typeof v === "object" && v !== null && !Array.isArray(v);

const trimOrNull = (v: unknown): string | null =>
  typeof v === "string" && v.trim() ? v.trim() : null;

/**
 * Normalise whatever is stored in tenant_configuration.live_widgets into a
 * complete band. null, the legacy `{weather, livescore}` shape, or anything
 * without a `band` object yields the defaults (disabled). Individual invalid
 * fields fall back to their defaults.
 */
export const normalizeLiveBand = (raw: unknown): NormalizedLiveBand => {
  const band = isObject(raw) ? raw.band : undefined;
  if (!isObject(band)) {
    return {
      ...DEFAULT_LIVE_BAND,
      widgets: [...DEFAULT_LIVE_BAND.widgets],
      durations: { ...DEFAULT_LIVE_BAND.durations },
      livescore: { ...DEFAULT_LIVE_BAND.livescore },
      news: { ...DEFAULT_LIVE_BAND.news },
      market: { ...DEFAULT_LIVE_BAND.market },
    };
  }
  const livescore = isObject(band.livescore) ? band.livescore : {};
  const news = isObject(band.news) ? band.news : {};
  const market = isObject(band.market) ? band.market : {};
  const symbols = Array.isArray(market.symbols)
    ? market.symbols.filter(
        (sym): sym is string => typeof sym === "string" && sym.trim() !== ""
      )
    : [];
  const storedWidgets = Array.isArray(band.widgets) ? band.widgets : [];
  const widgets = BAND_WIDGET_ORDER.filter((kind) => storedWidgets.includes(kind));
  const storedDurations = isObject(band.durations) ? band.durations : {};
  const durations = { ...DEFAULT_WIDGET_SECONDS };
  for (const kind of BAND_WIDGET_ORDER) {
    const value = storedDurations[kind];
    if ((ALLOWED_WIDGET_SECONDS as readonly unknown[]).includes(value)) {
      durations[kind] = value as number;
    }
  }
  return {
    enabled: band.enabled === true,
    placement: band.placement === "top" ? "top" : "bottom",
    heightPct: (ALLOWED_BAND_HEIGHT_PCTS as readonly number[]).includes(
      band.heightPct as number
    )
      ? (band.heightPct as number)
      : DEFAULT_BAND_HEIGHT_PCT,
    // None stored (or none valid) = all four, as the backend resolves it.
    widgets: widgets.length ? widgets : [...BAND_WIDGET_ORDER],
    durations,
    livescore: {
      sport: livescore.sport === "football" ? "football" : "cricket",
      matchId: trimOrNull(livescore.matchId),
    },
    news: {
      feedUrl: trimOrNull(news.feedUrl),
      category: (NEWS_CATEGORIES as readonly unknown[]).includes(news.category)
        ? (news.category as NewsCategory)
        : DEFAULT_NEWS_CATEGORY,
    },
    market: { symbols: symbols.length ? symbols : null },
  };
};

// Provider ticker notation, as the backend validates it: "^NSEI",
// "RELIANCE.NS", "EURINR=X", "BRK-B".
const MARKET_SYMBOL_RE = /^[A-Z0-9^.=-]{1,20}$/;

/** Split a comma/space separated symbol list: trimmed, uppercased, de-duplicated. */
export const parseMarketSymbols = (text: string): string[] => {
  const symbols: string[] = [];
  for (const part of text.split(/[\s,]+/)) {
    const symbol = part.trim().toUpperCase();
    if (symbol && !symbols.includes(symbol)) symbols.push(symbol);
  }
  return symbols;
};

/** Why a symbol list would be rejected by the backend, or null when it is fine. */
export const marketSymbolsError = (text: string): string | null => {
  const symbols = parseMarketSymbols(text);
  const bad = symbols.find((symbol) => !MARKET_SYMBOL_RE.test(symbol));
  if (bad) return `"${bad}" is not a valid ticker`;
  if (symbols.length > MAX_MARKET_SYMBOLS) {
    return `At most ${MAX_MARKET_SYMBOLS} symbols`;
  }
  return null;
};

/** Empty/blank is valid (server default); otherwise must be an http(s) URL. */
export const isValidFeedUrl = (value: string): boolean => {
  const v = value.trim();
  if (!v) return true;
  try {
    const url = new URL(v);
    return url.protocol === "http:" || url.protocol === "https:";
  } catch {
    return false;
  }
};

/** Pixel height left for the creative on a screen of the given height. */
export const remainingHeightPx = (screenHeight: number, heightPct: number) =>
  screenHeight - Math.round((screenHeight * heightPct) / 100);

export interface Tenant {
  id: string;
  [key: string]: unknown; // Tenant type is not fully defined in GraphQL schema
}

export interface TenantConfiguration {
  id: string;
  tenantId: string;
  tenantPlaylistCreativesPerSlot: number | null;
  clientPlaylistCreativeLimit: number | null;
  clientPlaylistCreativesPerSlot: number | null;
  clientCreativePlayDuration: number | null;
  tenantCreativePlayDuration: number | null;
  liveWidgets?: TenantLiveWidgets | null;
  createdAt: string;
  updatedAt: string;
  tenant?: Tenant;
}

// Note: CreateTenantConfigurationInput is deprecated - backend no longer supports create mutation
// Configuration is created automatically when using update mutation
export interface CreateTenantConfigurationInput {
  tenantId: string;
  tenantPlaylistCreativesPerSlot?: number;
  clientPlaylistCreativeLimit?: number;
  clientPlaylistCreativesPerSlot?: number;
  clientCreativePlayDuration?: number;
  tenantCreativePlayDuration?: number;
}

// Input for update mutation (matches UpdateTenantConfigurationInput)
export interface UpdateTenantConfigurationInput {
  tenantPlaylistCreativesPerSlot?: number;
  clientPlaylistCreativeLimit?: number;
  clientPlaylistCreativesPerSlot?: number;
  clientCreativePlayDuration?: number;
  tenantCreativePlayDuration?: number;
  liveWidgets?: TenantLiveWidgets | null;
}

export interface UpdateTenantConfigurationPayload {
  tenantId: string;
  configuration: UpdateTenantConfigurationInput;
}
